import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  Alert,
  Image
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { getUserPointHistory, transformPointHistoryData } from '../services/pointsApi';
import { fetchUserProfile } from '../services/userDataService';
import AppHeader from '../components/AppHeader';

const { width, height } = Dimensions.get('window');

// Function to format date from YYYY-MM-DD to DD-MM-YYYY
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return '';
  
  // Handle both YYYY-MM-DD format and potential existing DD-MM-YYYY
  const parts = dateString.split('-');
  
  if (parts.length === 3) {
    // If it's already in YYYY-MM-DD format, convert to DD-MM-YYYY
    if (parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    // If it's already in DD-MM-YYYY format, return as is
    else if (parts[2].length === 4) {
      return dateString;
    }
  }
  
  // Return original if format is unexpected
  return dateString;
};

export default function PointsScreen() {
  const navigation = useNavigation();
  const [activeTab, setActiveTab] = useState('history');
  const [userData, setUserData] = useState(null);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [rewards, setRewards] = useState([]);

  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState('Semua');

  useEffect(() => {
    loadRewards();
  }, []);

  useFocusEffect(
    useCallback(() => {
      let alive = true;
      const refresh = async () => {
        try {
          const userToken = await AsyncStorage.getItem('userToken');
          const sessionId = await AsyncStorage.getItem('session_id');
          if (!userToken || !sessionId) {
            navigation.navigate('Login');
            return;
          }

          try {
            const response = await fetchUserProfile();
            if (!alive || !response?.data) {
              return;
            }
            const apiUserData = response.data;
            setUserData({
              ...apiUserData,
              name: apiUserData.user_fullname || apiUserData.name || 'User',
              points: apiUserData.points || 0,
              stamps: apiUserData.stamps || 0,
            });
            return;
          } catch (backendError) {
            console.log('Backend fetch failed, using cached data:', backendError.message);
          }

          const userDataString = await AsyncStorage.getItem('userData');
          if (alive && userDataString) {
            setUserData(JSON.parse(userDataString));
          }
        } catch (error) {
          console.log('Error loading user data:', error);
          navigation.navigate('Login');
        }
      };
      void refresh();
      return () => {
        alive = false;
      };
    }, [navigation])
  );

  useEffect(() => {
    if (userData !== null) {
      loadPointsHistory(1);
    }
  }, [userData]);

  const loadPointsHistory = async (page = 1) => {
    try {
      setLoading(true);
      const response = await getUserPointHistory(page, 10);
      
      if (response.status === 'success' && response.data) {
        const transformedHistory = transformPointHistoryData(response.data);
        
        if (page === 1) {
          setPointsHistory(transformedHistory);
        } else {
          // Append new data for pagination
          setPointsHistory(prev => [...prev, ...transformedHistory]);
        }
        
        setPagination(response.data.pagination);
        setCurrentPage(page);
      }
    } catch (error) {
      console.log('Error loading points history:', error.message);
      // No fallback to mock data - show empty state instead
      if (page === 1) {
        setPointsHistory([]);
      }
    } finally {
      setLoading(false);
    }
  };



  const loadRewards = () => {
    // Simulate available rewards
    const availableRewards = [
     
    ];
    
    setRewards(availableRewards);
  };

  const handleRedeemReward = (reward) => {

    if (!userData || userData.points < reward.pointsCost) {
      Alert.alert(
        'Insufficient Points',
        `You need ${reward.pointsCost} points to redeem this reward. You currently have ${userData?.points || 0} points.`
      );
      return;
    }

    Alert.alert(
      'Redeem Reward',
      `Are you sure you want to redeem "${reward.title}" for ${reward.pointsCost} points?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Redeem',
          onPress: async () => {
            try {
              // Update user points
              const updatedUserData = {
                ...userData,
                points: userData.points - reward.pointsCost,
              };
              
              await AsyncStorage.setItem('userData', JSON.stringify(updatedUserData));
              setUserData(updatedUserData);
              
              // Add to points history
              const newHistoryItem = {
                id: Date.now(),
                type: 'redeemed',
                title: 'Reward Redemption',
                description: reward.title,
                points: -reward.pointsCost,
                date: new Date().toISOString().split('T')[0],
                time: new Date().toLocaleTimeString('en-US', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: false 
                }),
                location: 'Mobile App',
                icon: 'gift',
              };
              
              setPointsHistory(prev => [newHistoryItem, ...prev]);
              
              Alert.alert(
                'Success!', 
                `You've successfully redeemed "${reward.title}". Check your email for redemption details.`
              );
            } catch (error) {
              Alert.alert('Error', 'Failed to redeem reward. Please try again.');
            }
          },
        },
      ]
    );
  };

  const renderPointsHeader = () => (
    <View style={styles.pointsHeaderContainer}>
      <View style={styles.pointCard}>
        <View style={styles.pointCardBackground}>
          <View style={styles.subtleCircle} />
          <View style={styles.pointCardContent}>
            <View style={styles.pointCardLeft}>
              <Text style={styles.totalPointLabel}>Total Point Reward</Text>
              <Text style={styles.pointRewardLabel}>Point Reward</Text>
            </View>
            <View style={styles.pointCardRight}>
              <View style={styles.coinContainer}>
                <Image 
                  source={require('../../assets/icons/z-point-solid.svg')} 
                  style={{ 
                    width: 34, 
                    height: 34, 
                    tintColor: "#FFFFFF"
                  }}
                  resizeMode="contain"
                />
              </View>
              <Text style={styles.pointValue}>{userData?.points || 0}</Text>
              <Text style={styles.pointUnit}>pt</Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  const renderHistoryItem = (item) => (
    <View key={item.id} style={styles.historyItem}>
      <View style={[styles.historyIcon, 
        item.type === 'earned' ? styles.earnedIcon : styles.redeemedIcon
      ]}>
        <Ionicons 
          name={item.icon} 
          size={20} 
          color={item.type === 'earned' ? '#4CAF50' : '#FF6B35'} 
        />
      </View>
      
      <View style={styles.historyContent}>
      <Text style={styles.historyDescription} numberOfLines={1}>{item.description}</Text>
        <Text style={styles.historyTitle}>{item.title}</Text>
        
        <View style={styles.historyMeta}>
          <Text style={styles.historyDate}>{formatDateToDDMMYYYY(item.date)}</Text>
        </View>
      </View>
      
      <Text style={[
        styles.historyPoints,
        item.type === 'earned' ? styles.earnedPoints : styles.redeemedPoints
      ]}>
        {item.points > 0 ? '+' : ''}{item.points}
      </Text>
    </View>
  );

  const renderRewardCard = (reward) => (
    <TouchableOpacity
      key={reward.id}
      style={styles.rewardCard}
      onPress={() => handleRedeemReward(reward)}
    >
      <LinearGradient
        colors={[reward.color, reward.color + '80']}
        style={styles.rewardGradient}
      >
        <View style={styles.rewardHeader}>
          <View style={styles.rewardIcon}>
            <Ionicons name={reward.icon} size={24} color="#FFFFFF" />
          </View>
          <View style={styles.rewardBadge}>
            <Text style={styles.rewardBadgeText}>{reward.category}</Text>
          </View>
        </View>
        
        <View style={styles.rewardContent}>
          <Text style={styles.rewardTitle}>{reward.title}</Text>
          <Text style={styles.rewardDescription}>{reward.description}</Text>
        </View>
        
        <View style={styles.rewardFooter}>
          <View style={styles.rewardPoints}>
            <Ionicons name="star" size={16} color="#FFFFFF" />
            <Text style={styles.rewardPointsText}>{reward.pointsCost} pts</Text>
          </View>
          <Text style={styles.rewardAvailability}>{reward.availability}</Text>
        </View>
      </LinearGradient>
    </TouchableOpacity>
  );

  if (!userData) {
    return (
      <View style={styles.loadingContainer}>
        <Text>Loading...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <AppHeader
        title="Point Rewards"
        onBack={() => navigation.goBack()}
        right={
          <TouchableOpacity
            onPress={() => navigation.navigate('PointExpiry')}
            hitSlop={8}
            accessibilityLabel="Point kadaluwarsa"
          >
            <Ionicons name="time-outline" size={22} color="#1E293B" />
          </TouchableOpacity>
        }
      />
      
      {renderPointsHeader()}

      <TouchableOpacity
        style={styles.expiryBanner}
        onPress={() => navigation.navigate('PointExpiry')}
        activeOpacity={0.85}
      >
        <View style={styles.expiryBannerLeft}>
          <Ionicons name="alarm-outline" size={20} color="#B45309" />
          <Text style={styles.expiryBannerText}>Cek poin yang akan kadaluwarsa</Text>
        </View>
        <Ionicons name="chevron-forward" size={18} color="#B45309" />
      </TouchableOpacity>
      
      <View style={styles.historySection}>
        <View style={styles.historyHeader}>
          <Text style={styles.historyTitle}>Riwayat Point</Text>
          {/* Temporary hidden cause the feature is not ready yet */}
          {/*<TouchableOpacity style={styles.filterButton}>
            <Ionicons name="funnel-outline" size={16} color="333" />
            <Text style={styles.filterButtonText}>7 hari terakhir</Text>
          </TouchableOpacity>*/}
        </View>
        
        {/* <View style={styles.filterContainer}>
          {['Semua', 'Masuk', 'Redeem'].map((filter) => (
            <TouchableOpacity
              key={filter}
              style={[
                styles.filterBadge,
                activeFilter === filter && styles.activeFilterBadge
              ]}
              onPress={() => setActiveFilter(filter)}
            >
              <Text style={[
                styles.filterBadgeText,
                activeFilter === filter && styles.activeFilterBadgeText
              ]}>
                {filter}
              </Text>
            </TouchableOpacity>
          ))}
        </View> */}
      </View>
      
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.historyContainer}>
          {pointsHistory.length > 0 ? (
            <>
              {pointsHistory.map(renderHistoryItem)}
              {pagination && pagination.has_next && (
                <TouchableOpacity 
                  style={styles.loadMoreButton}
                  onPress={() => loadPointsHistory(currentPage + 1)}
                  disabled={loading}
                >
                  {loading ? (
                    <Text style={styles.loadMoreText}>Loading...</Text>
                  ) : (
                    <Text style={styles.loadMoreText}>Load More</Text>
                  )}
                </TouchableOpacity>
              )}
            </>
          ) : (
            <View style={styles.emptyState}>
              <Ionicons name="time-outline" size={64} color="#CCC" />
              <Text style={styles.emptyStateText}>No points history</Text>
              <Text style={styles.emptyStateSubtext}>Start earning points by scanning QR codes!</Text>
            </View>
          )}
        </View>
        
        <View style={styles.bottomSpacing} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  pointsHeaderContainer: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  expiryBanner: {
    marginHorizontal: 20,
    marginBottom: 20,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#FFFBEB',
    borderWidth: 1,
    borderColor: '#FDE68A',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  expiryBannerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  expiryBannerText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#92400E',
    flex: 1,
  },
  pointCard: {
    backgroundColor: '#F59E0B',
    borderRadius: 12,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    elevation: 5,
    height: 84,
  },
  pointCardBackground: {
    position: 'relative',
    overflow: 'hidden',
    flex: 1,
  },
  subtleCircle: {
    position: 'absolute',
    width: 213,
    height: 213,
    borderRadius: 106.5,
    borderWidth: 24,
    borderColor: '#FFFFFF',
    opacity: 0.24,
    left: -51,
    top: -43,
  },
  pointCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    flex: 1,
  },
  pointCardLeft: {
    flex: 1,
  },
  totalPointLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#F8FAFC',
    marginBottom: 4,
  },
  pointRewardLabel: {
    fontSize: 18,
    fontWeight: '500',
    color: '#F8FAFC',
  },
  pointCardRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  coinContainer: {
    width: 42,
    height: 42,
    backgroundColor: 'rgba(255, 255, 255, 0.24)',
    borderRadius: 999,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 9,
  },
  coinIcon: {
    width: 24,
    height: 24,
    backgroundColor: '#FEF3C7',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#FBBF24',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: '0px 4px 4px rgba(255, 255, 255, 0.5)',
    elevation: 2,
  },
  pointValue: {
    fontSize: 30,
    fontWeight: '600',
    color: '#F8FAFC',
  },
  pointUnit: {
    fontSize: 20,
    fontWeight: '400',
    color: '#F8FAFC',
  },
   historySection: {
    paddingHorizontal: 20,
    paddingBottom: 15,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderWidth: 1,
    borderColor: '#333',
    borderRadius: 6,
    gap: 4,
  },
  filterButtonText: {
    fontSize: 12,
    color: '#333',
    fontWeight: '500',
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  filterBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: 'rgba(165, 165, 165, 0.1)',
  },
  activeFilterBadge: {
    backgroundColor: '#000',
  },
  filterBadgeText: {
    fontSize: 12,
    color: '#000',
    fontWeight: '500',
  },
  activeFilterBadgeText: {
    color: '#FFFFFF',
  },
  historyTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
  },
  historyContainer: {
    paddingHorizontal: 20,
  },
  historyItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 15,
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  historyIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  earnedIcon: {
    backgroundColor: '#F1F8E9',
  },
  redeemedIcon: {
    backgroundColor: '#FFF3F0',
  },
  historyContent: {
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  historyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  historyDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  historyMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  historyLocation: {
    fontSize: 12,
    color: '#999',
  },
  historyDate: {
    fontSize: 12,
    color: '#999',
  },
  historyPoints: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  earnedPoints: {
    color: '#4CAF50',
  },
  redeemedPoints: {
    color: '#FF6B35',
  },
  rewardsContainer: {
    gap: 15,
  },
  rewardCard: {
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
    elevation: 5,
  },
  rewardGradient: {
    padding: 20,
  },
  rewardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
  },
  rewardIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  rewardBadge: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  rewardBadgeText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '600',
  },
  rewardContent: {
    marginBottom: 15,
  },
  rewardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  rewardDescription: {
    color: 'rgba(255, 255, 255, 0.9)',
    fontSize: 14,
    lineHeight: 20,
  },
  rewardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rewardPoints: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  rewardPointsText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 4,
  },
  rewardAvailability: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#666',
    marginTop: 15,
    marginBottom: 5,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  bottomSpacing: {
    height: 20,
  },
  loadMoreButton: {
    backgroundColor: '#FFFFFF',
    marginVertical: 15,
    paddingVertical: 15,
    borderRadius: 12,
    alignItems: 'center',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  loadMoreText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
});